<script lang="ts">
    import { getTemplates } from "../../../api"
    import { textareaAutoHeight, tooltip } from "../../../helper"
    import { createEventDispatcher } from "svelte"
    import { _ } from "svelte-i18n"
    import Modal from "../../widgets/Modal.svelte"
    import DocumentTemplateTextContent from "../../widgets/document-template-textContent.svelte"
    // @ts-ignore
    import hash from "object-hash"
    import Select from "svelte-select"
    import { templateSources } from "../../../functions/templates/helper"
    import { mdiAlertCircleOutline } from "@mdi/js"
    import Icon from "../../widgets/Icon.svelte"
    import { apiBaseURL, placeholderHint } from "../../../config"

    export let show = false
    export let operation: OperationOut

    let originalOperation: OperationOut

    let operationHash: string
    let templateChanged = false
    let baseTemplates: any[] = []
    let selectedBaseTemplate: {
        value: string
        label: string
        img: string
        template: DocumentTemplate
    } = { value: null, label: "", img: "", template: null }
    let updateHash = false
    const dispatcher = createEventDispatcher()
    getTemplates(operation.type == 1 ? "offer" : "orderConfirmation").then((res) => {
        baseTemplates = res.data.map((t) => {
            let templateSource = templateSources.find((ts) => ts.value == t.source)
            return {
                ...(templateSource || {}),
                label: `${templateSource?.label} - ${$_("address.dealerNr")} : ${t.dealerNumber}`,
                value: t.id,
                template: t,
            }
        })
        selectedBaseTemplate = baseTemplates.find((t) => t.value == operation?.baseTemplate) || baseTemplates[0]
        if (!selectedBaseTemplate.template.texts.contact) selectedBaseTemplate.template.texts.contact = {}
        selectedBaseTemplate.template.texts.contact.useLoggedInUserAsDefault = true
    })
    if (!operation?.documentTemplateTextContent) operation.documentTemplateTextContent = {}

    if (!operation?.documentTemplateTextContent?.contact)
        operation.documentTemplateTextContent.contact = {
            useLoggedInUserAsDefault: true,
            name: "",
            email: "",
            phone: "",
            webUrl: "",
        }
    operation.documentTemplateTextContent.contact.useLoggedInUserAsDefault = !(
        operation.documentTemplateTextContent.contact.useLoggedInUserAsDefault === false
    )
    templateChanged = false
    originalOperation = JSON.parse(JSON.stringify(operation))
    operationHash = hash(operation)

    $: {
        if (operationHash != hash(operation) && operationHash) {
            templateChanged = true
        } else {
            templateChanged = false
        }
    }

    $: if (selectedBaseTemplate?.value && operation?.baseTemplate != selectedBaseTemplate?.value) {
        if (!selectedBaseTemplate.template.texts.contact) selectedBaseTemplate.template.texts.contact = {}
        selectedBaseTemplate.template.texts.contact.useLoggedInUserAsDefault = true
        if (!operation.baseTemplate) {
            operation.baseTemplate = selectedBaseTemplate.value
            operation.documentTemplateTextContent = JSON.parse(
                JSON.stringify(selectedBaseTemplate?.template?.texts || {})
            )
            operationHash = hash(operation)
        } else {
            operation.baseTemplate = selectedBaseTemplate.value
            operation.documentTemplateTextContent = JSON.parse(
                JSON.stringify(selectedBaseTemplate?.template?.texts || {})
            )
        }
    }
    $: if (updateHash) {
        operationHash = hash(operation)
        updateHash = false
    }
    function createAlignment(
        alignment: string,
        labels: { [x: string]: any; left?: string; right?: string; top?: string; bottom?: string }
    ) {
        return {
            value: alignment,
            label: labels[alignment],
        }
    }

    // Define label mappings
    const horizontalLabels = {
        left: $_("components.documentTemplate.left"),
        right: $_("components.documentTemplate.right"),
    }
    const verticalLabels = {
        top: $_("components.documentTemplate.top"),
        bottom: $_("components.documentTemplate.bottom"),
    }

    // Initial values
    let horizontalAlignment = createAlignment(
        selectedBaseTemplate?.template?.pdfLayout?.logoPosition?.horizontalAlignment,
        horizontalLabels
    )
    let verticalAlignment = createAlignment(
        selectedBaseTemplate?.template?.pdfLayout?.logoPosition?.verticalAlignment,
        verticalLabels
    )

    // Reactive statements
    $: horizontalAlignment = createAlignment(
        selectedBaseTemplate?.template?.pdfLayout?.logoPosition?.horizontalAlignment,
        horizontalLabels
    )
    $: verticalAlignment = createAlignment(
        selectedBaseTemplate?.template?.pdfLayout?.logoPosition?.verticalAlignment,
        verticalLabels
    )
</script>

<Modal {show} size="full" on:close="{() => (show = false)}" showCloseIcon="{false}">
    <div slot="header">{$_("components.documentTemplate.editTemplate")}</div>
    {#if baseTemplates.length > 1}
        <Select
            inputAttributes="{{ autocomplete: 'off' }}"
            placeholder=""
            showChevron="{true}"
            clearable="{false}"
            items="{baseTemplates}"
            bind:value="{selectedBaseTemplate}"
        >
            <div slot="item" let:item let:index style="display: flex; align-items: center;">
                <div class="icon">
                    <img src="{item.img}" width="18" alt="" />
                </div>
                <span style="margin-left: 10px;"> {item?.label}</span>
            </div>
            <div slot="prepend" style="margin-right: 10px; display: flex; align-items: center;">
                <div class="icon">
                    <img src="{selectedBaseTemplate?.img}" width="18" alt="" />
                </div>
            </div>
        </Select>
    {/if}
    <div>
        <section>
            {#if selectedBaseTemplate?.template}
                <div class="container">
                    <form>
                        <section>
                            <div class="section-title no-space">
                                <span>{$_("components.documentTemplate.letterPaper")}</span>
                            </div>

                            {#if selectedBaseTemplate?.template?.pdfLayout?.simpleLayout}
                                <div class="row">
                                    <div class="col-sm-6 mt-md">
                                        <div>
                                            {#if selectedBaseTemplate?.template?.pdfLayout?.logoImage}
                                                {#if selectedBaseTemplate?.template?.pdfLayout?.logoImage?.src?.includes("data:image")}
                                                    <img
                                                        class="bordered"
                                                        src="{selectedBaseTemplate?.template?.pdfLayout?.logoImage
                                                            ?.src}"
                                                        alt="{selectedBaseTemplate?.template?.pdfLayout?.logoImage
                                                            ?.path}"
                                                    />
                                                {:else}
                                                    <img
                                                        class="bordered mb-sm"
                                                        src="{apiBaseURL}document-template/{selectedBaseTemplate?.value}/{selectedBaseTemplate
                                                            ?.template?.pdfLayout?.logoImage
                                                            .src}?cacheBuster={Date.now()}"
                                                        alt="{selectedBaseTemplate?.template?.pdfLayout?.logoImage
                                                            ?.path}"
                                                    />
                                                {/if}
                                                <div class="logo-alignment">
                                                    <div class="section-title">
                                                        {$_("components.documentTemplate.logoPosition")}
                                                    </div>
                                                    <div class="layout layout-wrap">
                                                        <div class="layout layout-wrap layout-gap-md">
                                                            <div
                                                                class="inputholder haslabel has-append no-input-margin"
                                                            >
                                                                <label for="">
                                                                    {$_(
                                                                        "components.documentTemplate.verticalOrientation"
                                                                    )}
                                                                </label>
                                                                <Select
                                                                    clearable="{false}"
                                                                    showChevron
                                                                    value="{verticalAlignment}"
                                                                    disabled="{true}"
                                                                    items="{[
                                                                        {
                                                                            value: 'top',
                                                                            label: $_(
                                                                                'components.documentTemplate.top'
                                                                            ),
                                                                        },
                                                                        {
                                                                            value: 'bottom',
                                                                            label: $_(
                                                                                'components.documentTemplate.bottom'
                                                                            ),
                                                                        },
                                                                    ]}"
                                                                />
                                                            </div>

                                                            <div
                                                                class="inputholder haslabel has-append no-input-margin"
                                                            >
                                                                <label for="">
                                                                    {$_(
                                                                        "components.documentTemplate.horizontalOrientation"
                                                                    )}</label
                                                                >

                                                                <Select
                                                                    clearable="{false}"
                                                                    showChevron
                                                                    value="{horizontalAlignment}"
                                                                    disabled="{true}"
                                                                    items="{[
                                                                        {
                                                                            value: 'right',
                                                                            label: $_(
                                                                                'components.documentTemplate.right'
                                                                            ),
                                                                        },
                                                                        {
                                                                            value: 'left',
                                                                            label: $_(
                                                                                'components.documentTemplate.left'
                                                                            ),
                                                                        },
                                                                    ]}"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div class="layout layout-wrap layout-gap-md">
                                                            <div
                                                                class="inputholder haslabel has-append no-input-margin"
                                                            >
                                                                <label for=""
                                                                    >{$_(
                                                                        "components.documentTemplate.verticalOrientation"
                                                                    )}</label
                                                                >
                                                                <input
                                                                    type="number"
                                                                    step="1"
                                                                    pattern="[0-9]"
                                                                    min="0"
                                                                    placeholder="0"
                                                                    value="{selectedBaseTemplate?.template?.pdfLayout
                                                                        ?.logoPosition?.verticalOffset}"
                                                                    disabled="{true}"
                                                                    id="left-margin"
                                                                />
                                                                <div class="input-append">mm</div>
                                                            </div>
                                                            <div
                                                                class="inputholder haslabel has-append no-input-margin"
                                                            >
                                                                <label for=""
                                                                    >{$_(
                                                                        "components.documentTemplate.horizontalOrientation"
                                                                    )}</label
                                                                >
                                                                <input
                                                                    type="number"
                                                                    step="1"
                                                                    pattern="[0-9]"
                                                                    min="0"
                                                                    placeholder="0"
                                                                    value="{selectedBaseTemplate?.template?.pdfLayout
                                                                        ?.logoPosition?.horizontalOffset}"
                                                                    disabled="{true}"
                                                                    id="bottom-margin"
                                                                />
                                                                <div class="input-append">mm</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <br />
                                            {:else}
                                                <div class="mb-md alert alert-warning">
                                                    <Icon path="{mdiAlertCircleOutline}" />
                                                    {$_("components.documentTemplate.noLogoAvailable")}
                                                </div>
                                            {/if}
                                        </div>
                                    </div>
                                    <div class="col-sm-6 mt-md">
                                        <div>
                                            {#if selectedBaseTemplate?.template?.pdfLayout.footerImage}
                                                {#if selectedBaseTemplate?.template?.pdfLayout.footerImage.src.includes("data:image")}
                                                    <img
                                                        class="bordered"
                                                        src="{selectedBaseTemplate?.template?.pdfLayout?.footerImage
                                                            ?.src}"
                                                        alt="{selectedBaseTemplate?.template?.pdfLayout?.footerImage
                                                            ?.path}"
                                                    />
                                                {:else}
                                                    <img
                                                        class="bordered mb-sm"
                                                        src="{apiBaseURL}document-template/{selectedBaseTemplate?.value}/{selectedBaseTemplate
                                                            ?.template?.pdfLayout?.footerImage
                                                            ?.src}?cacheBuster={Date.now()}"
                                                        alt="{selectedBaseTemplate?.template?.pdfLayout?.footerImage
                                                            ?.path}"
                                                    />
                                                {/if}
                                            {:else}
                                                <div class="mb-md alert alert-warning">
                                                    <Icon path="{mdiAlertCircleOutline}" />
                                                    {$_("components.documentTemplate.noFooterAvailable")}
                                                </div>
                                            {/if}
                                        </div>
                                    </div>
                                </div>
                            {:else}
                                <div class="row">
                                    <div class="col-sm-6 mt-md">
                                        <div>
                                            {#if selectedBaseTemplate?.template?.pdfLayout?.backgroundImage1}
                                                {#if selectedBaseTemplate?.template?.pdfLayout?.backgroundImage1?.src?.includes("data:image")}
                                                    <img
                                                        class="bordered"
                                                        src="{selectedBaseTemplate?.template?.pdfLayout
                                                            ?.backgroundImage1?.src}"
                                                        alt="{selectedBaseTemplate?.template?.pdfLayout
                                                            ?.backgroundImage1?.path}"
                                                    />
                                                {:else}
                                                    <img
                                                        class="bordered mb-sm"
                                                        src="{apiBaseURL}document-template/{selectedBaseTemplate?.value}/{selectedBaseTemplate
                                                            ?.template?.pdfLayout?.backgroundImage1
                                                            ?.src}?cacheBuster={Date.now()}"
                                                        alt="{selectedBaseTemplate?.template?.pdfLayout
                                                            ?.backgroundImage1?.path}"
                                                    />
                                                {/if}
                                            {:else}
                                                <div class="mb-md alert alert-warning">
                                                    <Icon path="{mdiAlertCircleOutline}" />
                                                    {$_("components.documentTemplate.noFooterAvailable")}
                                                </div>
                                            {/if}
                                        </div>
                                    </div>
                                    <div class="col-sm-6 mt-md">
                                        <div>
                                            {#if selectedBaseTemplate?.template?.pdfLayout.backgroundImageN}
                                                {#if selectedBaseTemplate?.template?.pdfLayout.backgroundImageN.src.includes("data:image")}
                                                    <img
                                                        class="bordered"
                                                        src="{selectedBaseTemplate?.template?.pdfLayout
                                                            ?.backgroundImageN?.src}"
                                                        alt="{selectedBaseTemplate?.template?.pdfLayout
                                                            ?.backgroundImageN?.path}"
                                                    />
                                                {:else}
                                                    <img
                                                        class="bordered mb-sm"
                                                        src="{apiBaseURL}document-template/{selectedBaseTemplate?.value}/{selectedBaseTemplate
                                                            ?.template?.pdfLayout?.backgroundImageN
                                                            .src}?cacheBuster={Date.now()}"
                                                        alt="{selectedBaseTemplate?.template?.pdfLayout
                                                            ?.backgroundImageN?.path}"
                                                    />
                                                {/if}
                                            {:else}
                                                <div class="mb-md alert alert-warning">
                                                    <Icon path="{mdiAlertCircleOutline}" />
                                                    {$_("components.documentTemplate.noTemplateAvailable")}
                                                </div>
                                            {/if}
                                        </div>
                                    </div>
                                </div>
                            {/if}
                        </section>

                        {#if operation?.documentTemplateTextContent}
                            <DocumentTemplateTextContent
                                bind:texts="{operation.documentTemplateTextContent}"
                                inProgress="{false}"
                                inProgressPlaceholder="{$_('general.beeingLoaded')}"
                                {placeholderHint}
                                {textareaAutoHeight}
                                showPreLetter="{true}"
                                originalPreLetter="{operation?.preLetter || null}"
                                on:updateHash="{() => {
                                    operationHash = hash(operation)
                                    templateChanged = false
                                }}"
                                type="{operation.type == 1 ? 'offer' : 'orderConfirmation'}"
                            />
                        {/if}
                        <section>
                            <div class="section-title">{$_("components.documentTemplate.margins")}</div>
                            <div class="layout layout-wrap layout-gap-md">
                                <div class="inputholder haslabel has-append no-input-margin">
                                    <label for=""
                                        >{$_("components.documentTemplate.top")}
                                        <small>{$_("components.documentTemplate.forSubsequentPages")}</small></label
                                    >
                                    <input
                                        type="number"
                                        step="1"
                                        pattern="[0-9]"
                                        min="0"
                                        placeholder="0"
                                        value="{selectedBaseTemplate?.template?.pdfLayout?.borders?.top}"
                                        disabled="{true}"
                                        id="top-margin"
                                    />
                                    <div class="input-append">mm</div>
                                </div>

                                <div class="inputholder haslabel has-append no-input-margin">
                                    <label for="">{$_("components.documentTemplate.right")}</label>
                                    <input
                                        type="number"
                                        step="1"
                                        pattern="[0-9]"
                                        min="0"
                                        placeholder="0"
                                        value="{selectedBaseTemplate?.template?.pdfLayout?.borders?.right}"
                                        disabled="{true}"
                                        id="right-margin"
                                    />
                                    <div class="input-append">mm</div>
                                </div>

                                <div class="inputholder haslabel has-append no-input-margin">
                                    <label for="">{$_("components.documentTemplate.bottom")}</label>
                                    <input
                                        type="number"
                                        step="1"
                                        pattern="[0-9]"
                                        min="0"
                                        placeholder="0"
                                        value="{selectedBaseTemplate?.template?.pdfLayout?.borders?.bottom}"
                                        disabled="{true}"
                                        id="bottom-margin"
                                    />
                                    <div class="input-append">mm</div>
                                </div>

                                <div class="inputholder haslabel has-append no-input-margin">
                                    <label for="">{$_("components.documentTemplate.left")}</label>
                                    <input
                                        type="number"
                                        step="1"
                                        pattern="[0-9]"
                                        min="0"
                                        placeholder="0"
                                        value="{selectedBaseTemplate?.template?.pdfLayout?.borders?.left}"
                                        disabled="{true}"
                                        id="left-margin"
                                    />
                                    <div class="input-append">mm</div>
                                </div>
                            </div>
                        </section>
                    </form>
                    <div class="legend">
                        <div class="headline">{$_("general.legend")}</div>
                        <div class="items">
                            <section>
                                <div class="item">
                                    <div class="icon">*</div>
                                    <div class="text">{$_("general.mandatoryField")}</div>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            {/if}
        </section>
    </div>
    <div slot="footer">
        <div style="display: flex; justify-content: space-between; width: 100%;">
            <button
                class="btn btn-secondary"
                on:click="{() => {
                    show = false
                }}">{$_("general.cancel")}</button
            >
            <div style="display: flex; flex-direction: column;  position: relative;">
                {#if templateChanged}
                    <button
                        type="button"
                        data-cy="cancel"
                        style="border: 0px; padding: 0px; background: transparent; position: absolute; top: -20px;"
                        use:tooltip="{{
                            content: $_('operations.resetChanges'),
                        }}"
                        on:click="{() => {
                            operation = JSON.parse(JSON.stringify(originalOperation))
                            originalOperation = JSON.parse(JSON.stringify(operation))
                            selectedBaseTemplate =
                                baseTemplates.find((t) => t.value == operation?.baseTemplate) || baseTemplates[0]
                            updateHash = true
                            templateChanged = false
                        }}"
                    >
                        {$_("general.reset")}
                    </button>
                {/if}

                <div
                    use:tooltip="{{
                        content: templateChanged ? 'invalid' : $_('components.documentTemplate.templateNotModified'),
                    }}"
                >
                    <button
                        class="btn"
                        data-cy="save"
                        disabled="{!templateChanged}"
                        on:click|preventDefault|stopPropagation="{() => {
                            show = false
                            dispatcher('save', { operation })
                        }}"
                    >
                        {$_("general.apply")}</button
                    >
                </div>
            </div>
        </div>
    </div>
</Modal>
